import { Injectable } from '@angular/core';
import {
  InsightsProducts,
  InsightsProductsFilter,
} from '@order/pages/order-insights/models/insights-products.model';
import { Product } from '@shared/models/product.model';
import { Order } from '@usf/ngrx-order';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class OrderInsightsPageHeaderService {
  constructor(private translateService: TranslateService) {}

  getLostLinesProducts(
    order: Order,
    productMap: Map<number, Product>,
  ): InsightsProducts {
    const products: Product[] =
      order?.orderHeader?.dyfProductsNotOrdered
        ?.map(productId => productMap.get(productId))
        .filter(product => !!product) || [];
    return {
      color: 'red',
      badgeColor: 'red-badge',
      products: products,
      badgeCount: 0,
      count: 0,
      title: 'Lost Lines',
    };
  }

  getNewProductsOrderedProducts(
    order: Order,
    productMap: Map<number, Product>,
  ): InsightsProducts {
    const products: Product[] =
      order?.orderHeader?.newProductsOrderedNotInRecentlyPurchased
        ?.map(productId => productMap.get(productId))
        .filter(product => !!product) || [];
    return {
      color: 'green',
      badgeColor: 'green-badge',
      products: products,
      badgeCount: 0,
      count: 0,
      title: 'New Products Ordered',
    };
  }

  getProductsInOrder(
    order: Order,
    productMap: Map<number, Product>,
  ): InsightsProducts {
    const products: Product[] = [];

    order?.orderItems?.forEach(orderItem => {
      const productNumber = orderItem.productNumber;
      const product = productMap.get(productNumber);

      if (product && !products.includes(product)) {
        products.push(product);
      }
    });

    // Handle new products ordered not in recently purchased
    order?.orderHeader?.newProductsOrderedNotInRecentlyPurchased?.forEach(
      productId => {
        const product = productMap.get(productId);
        if (product && !products.includes(product)) {
          products.push(product);
        }
      },
    );

    return {
      color: 'black',
      badgeColor: 'black-badge',
      products: products,
      badgeCount: products.length,
      count: products.length,
      title: 'Products in Order',
    };
  }

  getQuickFilters(
    lostLinesProducts: InsightsProducts,
    newProductsOrderedProducts: InsightsProducts,
    productsInOrder: InsightsProducts,
  ): InsightsProductsFilter[] {
    const allProducts = [
      lostLinesProducts,
      newProductsOrderedProducts,
      productsInOrder,
    ];
    const totalCount = allProducts.reduce(
      (sum, group) => sum + (group.products?.length || 0),
      0,
    );

    return [
      {
        id: 'all',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.allProducts',
        ),
        selected: true,
        insightsProducts: allProducts,
        badgeCount: 1,
        count: totalCount,
      },
      {
        id: 'lost-lines',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.lostLines',
        ),
        color: 'red',
        badgeColor: 'red-badge',
        badgeCount: lostLinesProducts.products.length,
        count: lostLinesProducts.products.length,
        selected: false,
        insightsProducts: [lostLinesProducts],
      },
      {
        id: 'new-products-ordered',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.newProductsOrdered',
        ),
        color: 'green',
        badgeColor: 'green-badge',
        badgeCount: newProductsOrderedProducts.products.length,
        count: newProductsOrderedProducts.products.length,
        selected: false,
        insightsProducts: [newProductsOrderedProducts],
      },
      {
        id: 'products-in-order',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.productsInOrder',
        ),
        badgeColor: 'black-badge',
        badgeCount: productsInOrder.products.length,
        count: productsInOrder.products.length,
        selected: false,
        insightsProducts: [productsInOrder],
      },
    ];
  }
}
